/**
 * ParetoChart — variants in (cost ✕ correctness) space (UI-SPEC §3.2).
 * Visualizes *why* the optimizer chose what it chose and what it's exploring:
 * active = solid, candidate/shadow/canary = ghosted, the frontier line drawn.
 * Lightweight inline SVG (no chart dependency).
 */

import * as React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../../card";

export interface ParetoPoint {
  version: number;
  state: string;
  cost: number; // x (lower better)
  correctness: number; // y (higher better), 0..1
}

const W = 360;
const H = 200;
const PAD = 28;

export function ParetoChart({ points }: { points: ParetoPoint[] }) {
  if (points.length === 0) {
    return (
      <Card>
        <CardHeader><CardTitle>Pareto frontier</CardTitle></CardHeader>
        <CardContent><p className="type-body text-muted-foreground">No scored variants yet — run an eval to plot the cost/correctness frontier.</p></CardContent>
      </Card>
    );
  }
  const maxCost = Math.max(...points.map((p) => p.cost), 0.0001);
  const x = (c: number) => PAD + (c / maxCost) * (W - 2 * PAD);
  const y = (k: number) => H - PAD - k * (H - 2 * PAD);

  // Frontier: sort by cost asc, keep points not dominated (no cheaper point with ≥ correctness).
  const sorted = [...points].sort((a, b) => a.cost - b.cost);
  const frontier: ParetoPoint[] = [];
  let bestK = -1;
  for (const p of sorted) {
    if (p.correctness > bestK) { frontier.push(p); bestK = p.correctness; }
  }

  return (
    <Card>
      <CardHeader><CardTitle>Pareto frontier (cost ✕ correctness)</CardTitle></CardHeader>
      <CardContent>
        <svg width={W} height={H} role="img" aria-label="Pareto chart of variants by cost and correctness">
          {/* axes */}
          <line x1={PAD} y1={H - PAD} x2={W - PAD} y2={H - PAD} className="stroke-border" />
          <line x1={PAD} y1={PAD} x2={PAD} y2={H - PAD} className="stroke-border" />
          <text x={W / 2} y={H - 4} textAnchor="middle" className="fill-muted-foreground type-subtext">cost →</text>
          <text x={10} y={H / 2} transform={`rotate(-90 10 ${H / 2})`} textAnchor="middle" className="fill-muted-foreground type-subtext">correctness →</text>
          {/* frontier line */}
          <polyline
            fill="none"
            className="stroke-success"
            strokeWidth={1}
            points={frontier.map((p) => `${x(p.cost)},${y(p.correctness)}`).join(" ")}
          />
          {/* points */}
          {points.map((p) => {
            const active = p.state === "active";
            const ghost = ["candidate", "shadow", "canary"].includes(p.state);
            return (
              <g key={p.version}>
                <circle
                  cx={x(p.cost)} cy={y(p.correctness)} r={active ? 5 : 4}
                  className={active ? "fill-[var(--chart-1)]" : ghost ? "fill-muted-foreground/50" : "fill-muted-foreground/25"}
                />
                <text x={x(p.cost) + 6} y={y(p.correctness) + 3} className="fill-muted-foreground type-subtext">v{p.version}</text>
              </g>
            );
          })}
        </svg>
      </CardContent>
    </Card>
  );
}
